import type { ActionType } from "typesafe-actions";
import { createAction, createAsyncAction } from "typesafe-actions";

import CustomFieldValue from "@mapmycustomers/shared/types/customField/CustomFieldValue";
import { Activity, Company, Deal, Person } from "@mapmycustomers/shared/types/entity";
import { RawFile } from "@mapmycustomers/shared/types/File";
import { OnlyRequiredFields } from "@mapmycustomers/shared/util";

import { RecurringActionType } from "@app/component/createEditEntity/Activity/enum/RecurringActionType";
import RecordPaneTab from "@app/enum/preview/RecordPaneTab";
import { FieldValidationErrors } from "@app/store/api/ApiService";
import ChangeAssociationsPayload from "@app/store/associations/ChangeAssociationsPayload";
import { NextAssociationsState } from "@app/store/associations/getNextAssociationsState";
import FileListItem from "@app/types/FileListItem";

import type ActivityRecordData from "./ActivityRecordData";

export const changeAssociatedEntities = createAsyncAction(
  "activityRecord/changeAssociatedEntities/request",
  "activityRecord/changeAssociatedEntities/success",
  "activityRecord/changeAssociatedEntities/failure"
)<
  Pick<ChangeAssociationsPayload, "company" | "deal" | "person">,
  Pick<NextAssociationsState, "availableCompanies" | "availableDeals" | "availablePeople">,
  void
>();

export const fetchChildActivities = createAsyncAction(
  "activityRecord/fetchChildActivities/request",
  "activityRecord/fetchChildActivities/success",
  "activityRecord/fetchChildActivities/failure"
)<{ loadMore?: boolean }, { activities: Activity[]; loadMore?: boolean; total: number }, void>();

export const fetchPreviewData = createAsyncAction(
  "activityRecord/fetchPreviewData/request",
  "activityRecord/fetchPreviewData/success",
  "activityRecord/fetchPreviewData/failure"
)<Activity["id"], { recordData: ActivityRecordData }, void>();

export const bulkUpdateFiles = createAsyncAction(
  "activityRecord/bulkUpdateFiles/request",
  "activityRecord/bulkUpdateFiles/success",
  "activityRecord/bulkUpdateFiles/failure"
)<{ callback?: () => void; files: OnlyRequiredFields<RawFile, "id">[] }, RawFile[], unknown>();

export const deleteActivity = createAsyncAction(
  "activityRecord/deleteActivity/request",
  "activityRecord/deleteActivity/success",
  "activityRecord/deleteActivity/failure"
)<{ callback?: () => void; deleteType?: RecurringActionType } | void, void, void>();

export const clearAllUploadedActivityFiles = createAction(
  "activityRecord/clearAllUploadedActivityFiles"
)<void>();

export const deleteActivityFile = createAsyncAction(
  "activityRecord/deleteActivityFile/request",
  "activityRecord/deleteActivityFile/success",
  "activityRecord/deleteActivityFile/failure"
)<
  RawFile,
  { activityId: Activity["id"]; file: RawFile; removed: boolean },
  { activityId: Activity["id"]; file: RawFile }
>();

export const downloadActivityFile = createAction("activityRecord/downloadActivityFile")<RawFile>();

export const fetchThumbnail = createAction("activityRecord/thumbnail")<{
  callback: (data: Blob) => void;
  fileId: RawFile["id"];
}>();

export const addActivityCompany = createAsyncAction(
  "activityRecord/addActivityCompany/request",
  "activityRecord/addActivityCompany/success",
  "activityRecord/addActivityCompany/failure"
)<
  { companyId: Company["id"]; id: Activity["id"] },
  { company: Company; nextAssociations: NextAssociationsState },
  void
>();

export const addActivityDeal = createAsyncAction(
  "activityRecord/addActivityDeal/request",
  "activityRecord/addActivityDeal/success",
  "activityRecord/addActivityDeal/failure"
)<
  { dealId: Deal["id"]; id: Activity["id"] },
  { deal: Deal; nextAssociations: NextAssociationsState },
  void
>();

export const setActivityPeople = createAsyncAction(
  "activityRecord/setActivityPeople/request",
  "activityRecord/setActivityPeople/success",
  "activityRecord/setActivityPeople/failure"
)<
  {
    id: Activity["id"];
    peopleIds: Person["id"][];
    primaryPersonId?: Person["id"];
  },
  { isMultiContactEnabled: boolean; nextAssociations: NextAssociationsState; people: Person[] },
  void
>();

export const relateUnrelateEntities = createAsyncAction(
  "activityRecord/relateUnrelateEntities/request",
  "activityRecord/relateUnrelateEntities/success",
  "activityRecord/relateUnrelateEntities/failure"
)<
  {
    associatedCompany?: Company;
    associatedDeal?: Deal;
    associatedPerson?: Person;
    isDealCorrectlyRelatedToCompany: boolean;
    isDealCorrectlyRelatedToPerson: boolean;
    isPersonCorrectlyRelatedToCompany: boolean;
    unrelate?: boolean;
  },
  { associationsState: NextAssociationsState; deal?: Deal; person?: Person; unrelate: boolean },
  void
>();

export const postponeActivity = createAsyncAction(
  "activityRecord/postponeActivity/request",
  "activityRecord/postponeActivity/success",
  "activityRecord/postponeActivity/failure"
)<{ activity: Activity; callback?: (activity: Activity) => void }, Activity, void>();

export const setActiveTab = createAction("activityRecord/setActiveTab")<RecordPaneTab>();

export const setHasChangesFlag = createAction("activityRecord/setHasChangesFlag")<boolean>();

export const removeActivityCompany = createAsyncAction(
  "activityRecord/removeActivityCompany/request",
  "activityRecord/removeActivityCompany/success",
  "activityRecord/removeActivityCompany/failure"
)<Activity["id"], NextAssociationsState, void>();

export const removeActivityDeal = createAsyncAction(
  "activityRecord/removeActivityDeal/request",
  "activityRecord/removeActivityDeal/success",
  "activityRecord/removeActivityDeal/failure"
)<Activity["id"], NextAssociationsState, void>();

export const toggleCompleteActivity = createAsyncAction(
  "activityRecord/toggleCompleteActivity/request",
  "activityRecord/toggleCompleteActivity/success",
  "activityRecord/toggleCompleteActivity/failure"
)<{ activity: Activity; callback?: (activity: Activity) => void }, Activity, void>();

export const uploadActivityFiles = createAsyncAction(
  "activityRecord/uploadActivityFiles/request",
  "activityRecord/uploadActivityFiles/success",
  "activityRecord/uploadActivityFiles/failure"
)<
  { callback?: (filesList: FileListItem[]) => void; fileGroupId?: string; files: (Blob | File)[] },
  FileListItem[],
  void
>();

export const updateActivity = createAsyncAction(
  "activityRecord/updateActivity/request",
  "activityRecord/updateActivity/success",
  "activityRecord/updateActivity/failure"
)<
  {
    activity: Record<string, unknown> & { id: Activity["id"] };
    callback?: (activity: Activity) => void;
    customFields?: Record<string, CustomFieldValue>;
    fieldsValueValidateCallback?: (fieldErrors: FieldValidationErrors[]) => void;
    layoutId: number;
    updateType?: RecurringActionType;
  },
  Activity,
  void
>();

export type Actions = ActionType<
  | typeof addActivityCompany
  | typeof addActivityDeal
  | typeof bulkUpdateFiles
  | typeof changeAssociatedEntities
  | typeof clearAllUploadedActivityFiles
  | typeof deleteActivity
  | typeof deleteActivityFile
  | typeof fetchChildActivities
  | typeof fetchPreviewData
  | typeof postponeActivity
  | typeof relateUnrelateEntities
  | typeof removeActivityCompany
  | typeof removeActivityDeal
  | typeof setActiveTab
  | typeof setActivityPeople
  | typeof setHasChangesFlag
  | typeof toggleCompleteActivity
  | typeof updateActivity
  | typeof uploadActivityFiles
>;
